import React, { useContext, useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { CarritoContext } from './context/CarritoContext';
import AlertLogin from './AlertLogin';
import './Carrito.css';

const Carrito = ({ onCarritoVacio }) => {
  const { carrito, setCarrito } = useContext(CarritoContext);
  const [mostrarDatosBancarios, setMostrarDatosBancarios] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [copiadoNumero, setCopiadoNumero] = useState(false);
  const [mostrarAlertLogin, setMostrarAlertLogin] = useState(false);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [montoTotal, setMontoTotal] = useState(0);
  const datosBancariosRef = useRef(null);
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [serverErrorMessage, setServerErrorMessage] = useState('');
  const [cuentaActiva, setCuentaActiva] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    const carritoLocal = JSON.parse(localStorage.getItem('carrito')) || [];
    const carritoConCantidad = carritoLocal.map(item => ({
      ...item,
      cantidad: item.cantidad ? item.cantidad : 1
    }));
    setCarrito(carritoConCantidad);
  }, [setCarrito]);

  const guardarCarrito = (nuevoCarrito) => {
    localStorage.setItem('carrito', JSON.stringify(nuevoCarrito));
    setCarrito(nuevoCarrito);
  };

  const actualizarCantidad = (id, tipo, cantidad) => {
    if (cantidad < 1) return;
    const nuevoCarrito = carrito.map(item =>
      item.id === id && item.tipo === tipo ? { ...item, cantidad } : item
    );
    guardarCarrito(nuevoCarrito);
  };

  const eliminarItem = (id, tipo) => {
    const nuevoCarrito = carrito.filter(item => !(item.id === id && item.tipo === tipo));
    guardarCarrito(nuevoCarrito);
    if (nuevoCarrito.length === 0 && onCarritoVacio) {
      onCarritoVacio();
    }
  };

  const calcularTotal = () => {
    return carrito.reduce((total, item) =>
      total + (item.precio * item.cantidad || 0), 0
    );
  };

  const calcularPuntosTotales = () => {
    return carrito.reduce((total, item) => {
      if (item.tipo === 'producto') {
        return total + (item.puntos_regalo * item.cantidad || 0);
      } else if (item.tipo === 'ticket') {
        return total + (item.puntos * item.cantidad || 0);
      }
      return total;
    }, 0);
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '0';
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  const copiarDatosBancarios = () => {
    if (!cuentaActiva) return;

    const datosBancarios = `MatiMarket - Datos de Pago:\nBanco: ${cuentaActiva.banco}\nTipo Cuenta: ${cuentaActiva.tipo_cuenta}\nNúmero Cuenta: ${cuentaActiva.numero}\nTitular: ${cuentaActiva.nombre}\nRUT: ${cuentaActiva.rut}\nEmail Comprobante: ${cuentaActiva.email}\nMonto a transferir: $${formatPrice(calcularTotal())}`;
    navigator.clipboard.writeText(datosBancarios)
      .then(() => {
        setCopiado(true);
        setTimeout(() => setCopiado(false), 3000);
      })
      .catch(() => alert('Error al copiar los datos bancarios'));
  };

  const copiarNumeroCuenta = () => {
    if (!cuentaActiva) return;
    navigator.clipboard.writeText(cuentaActiva.numero)
      .then(() => {
        setCopiadoNumero(true);
        setTimeout(() => setCopiadoNumero(false), 2500);
      })
      .catch(() => {});
  };

  const mostrarDatos = () => {
    setMostrarDatosBancarios(true);
    setTimeout(() => {
      if (datosBancariosRef.current) {
        datosBancariosRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const cancelarPago = () => {
    setMostrarDatosBancarios(false);
  };

  const ocultarAlertLogin = () => {
    setMostrarAlertLogin(false);
  };

  const transferenciaRealizada = async () => {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      setModalAbierto(false);
      setMostrarAlertLogin(true);
      return;
    }

    if (!user.id) {
      return;
    }

    try {
      setIsSubmitting(true);
      setServerErrorMessage('');

      const response = await fetch('/.netlify/functions/ingresarCompras', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ carrito, usuario: user.id }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Error al procesar la compra');
      }
      const codigoVenta = result.codigoVenta;

      setIsSuccess(true);
      setIsSubmitting(false);
      const puntosTotales = calcularPuntosTotales();
      await sendEmailCarrito(user.email, carrito, montoTotal, puntosTotales, codigoVenta);

      setTimeout(() => {
        localStorage.removeItem('carrito');
        setCarrito([]);
        if (onCarritoVacio) onCarritoVacio();
        setModalAbierto(false);
        navigate(result.link);
      }, 2000);
    } catch (error) {
      setServerErrorMessage(error.message);
      setIsSubmitting(false);
    }
  };

  const sendEmailCarrito = async (email, carrito, montoTotal, puntosTotales, codigoVenta) => {
    try {
      const response = await fetch('/.netlify/functions/sendEmailCarrito', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, carrito, montoTotal, puntosTotales, codigoVenta }),
      });

      if (!response.ok) {
        const responseData = await response.json();
        console.error('Error al enviar correo:', responseData.error);
      }
    } catch (error) {
      console.error('Error en el proceso de correo:', error);
    }
  };

  const handleAbrirModal = () => {
    setMontoTotal(calcularTotal());
    setModalAbierto(true);
  };

  const handleCerrarModal = () => {
    setModalAbierto(false);
  };

  useEffect(() => {
    const fetchCuentaActiva = async () => {
      try {
        const { data, error } = await supabase
          .from('cuentas')
          .select('*')
          .eq('estado', true)
          .single();

        if (error) throw error;
        setCuentaActiva(data);
      } catch (error) {
        console.error('Error al obtener la cuenta activa:', error);
      }
    };

    fetchCuentaActiva();
  }, []);

  const totalPuntos = calcularPuntosTotales();
  const totalMonto = calcularTotal();

  return (
    <div className="carrito-page-container">
      {mostrarAlertLogin && (
        <AlertLogin 
          description="Estás a un paso: inicia sesión o regístrate para validar tu compra"
          onClose={ocultarAlertLogin}
        />
      )}

      {/* Confirmation Modal */}
      {modalAbierto && (
        <div className="modal-custom-backdrop" onClick={handleCerrarModal}>
          <div className="modal-custom-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-custom-header">
              <span className="material-symbols-outlined text-[#b7f900] text-3xl">account_balance_wallet</span>
              <h3 className="text-xl font-bold text-white">Confirmar Transferencia</h3>
            </div>

            <p className="text-gray-300 text-sm my-4">
              ¿Confirmas que ya realizaste la transferencia por un monto exacto de{' '}
              <strong className="text-[#b7f900] font-semibold text-lg">${formatPrice(montoTotal)}</strong> a la cuenta indicada?
            </p>

            {serverErrorMessage && (
              <div className="p-3 mb-4 rounded-xl bg-red-950/60 border border-red-500/40 text-red-200 text-sm">
                <p>{serverErrorMessage}</p>
                {serverErrorMessage.includes('producto') && (
                  <Link to='/mis-pedidos' className="text-[#b7f900] underline font-medium block mt-1">
                    Ver mis pedidos aquí
                  </Link>
                )}
                {serverErrorMessage.includes('ticket') && (
                  <Link to='/mis-puntos' className="text-[#b7f900] underline font-medium block mt-1">
                    Ver mis puntos aquí
                  </Link>
                )}
              </div>
            )}

            <div className="modal-custom-actions">
              {!isSubmitting && !isSuccess && (
                <>
                  <button type="button" className="btn btn-ghost text-gray-400 hover:text-white" onClick={handleCerrarModal}>
                    No, volver
                  </button>
                  <button type="button" className="btn btn-primary text-black font-bold px-6" onClick={transferenciaRealizada}>
                    Sí, ya transferí
                  </button>
                </>
              )}

              {isSubmitting && (
                <div className="flex flex-col items-center py-4 w-full">
                  <span className="loading loading-spinner loading-lg text-[#b7f900]"></span>
                  <p className="text-sm text-gray-300 mt-2">Registrando compra y emitiendo comprobante...</p>
                </div>
              )}

              {!isSubmitting && isSuccess && (
                <div className="flex flex-col items-center py-4 w-full text-center">
                  <span className="material-symbols-outlined text-[#b7f900] text-6xl animate-bounce">
                    check_circle
                  </span>
                  <h4 className="text-lg font-bold text-white mt-2">¡Compra Registrada con Éxito!</h4>
                  <p className="text-xs text-gray-400 mt-1">Redirigiendo a tu panel...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cart Top Header */}
      <div className="carrito-hero-section">
        <div className="carrito-breadcrumbs">
          <Link to="/" className="text-gray-400 hover:text-white transition">Inicio</Link>
          <span className="text-gray-600">/</span>
          <span className="text-[#b7f900]">Carrito</span>
        </div>
        <h1 className="title-mm">Mi Carrito de Compras</h1>
        <p className="text-gray-400 text-sm max-w-xl mx-auto text-center mt-1">
          Revisa tus productos y tickets, confirma tu pedido y acumula puntos para los próximos sorteos.
        </p>

        {/* Steps indicator */}
        <div className="carrito-steps-bar">
          <div className="step-item active">
            <span className="step-num">1</span>
            <span className="step-label">Revisión</span>
          </div>
          <div className={`step-line ${mostrarDatosBancarios ? 'active' : ''}`} />
          <div className={`step-item ${mostrarDatosBancarios ? 'active' : ''}`}>
            <span className="step-num">2</span>
            <span className="step-label">Transferencia</span>
          </div>
          <div className="step-line" />
          <div className="step-item">
            <span className="step-num">3</span>
            <span className="step-label">Confirmación</span>
          </div>
        </div>
      </div>

      {/* Cart Content */}
      {carrito.length === 0 ? (
        <div className="carrito-empty-state">
          <div className="empty-cart-icon-wrapper">
            <span className="material-symbols-outlined empty-cart-icon">shopping_bag</span>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Tu carrito está vacío</h2>
          <p className="text-gray-400 max-w-md mx-auto mb-8 text-sm">
            Explora nuestro catálogo de productos exclusivos o compra tickets para ganar puntos y participar en los sorteos oficiales.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/productos" className="btn btn-primary text-black font-semibold">
              <span className="material-symbols-outlined text-lg mr-1">storefront</span>
              Explorar Catálogo
            </Link>
            <Link to="/comprar-tickets" className="btn btn-outline border-[#b7f900] text-[#b7f900] hover:bg-[#b7f900] hover:text-black">
              <span className="material-symbols-outlined text-lg mr-1">confirmation_number</span>
              Comprar Tickets
            </Link>
          </div>
        </div>
      ) : (
        <div className="carrito-layout-grid">
          {/* Left Column: Items List */}
          <div className="carrito-items-column">
            <div className="carrito-items-header">
              <span className="text-gray-300 font-medium">
                Artículos ({carrito.reduce((sum, it) => sum + (it.cantidad || 1), 0)})
              </span>
              <button
                type="button"
                className="text-xs text-red-400 hover:text-red-300 transition flex items-center gap-1"
                onClick={() => {
                  guardarCarrito([]);
                  if (onCarritoVacio) onCarritoVacio();
                }}
              >
                <span className="material-symbols-outlined text-sm">delete_sweep</span>
                Vaciar carrito
              </button>
            </div>

            <div className="carrito-items-cards-list">
              {carrito.map((item, index) => {
                const isTicket = item.tipo === 'ticket';
                const itemImg = item.imagen_1 || (isTicket ? '/img/ticket-default.png' : '/img/placeholder-product.png');
                const puntosItem = isTicket
                  ? (item.puntos || 0) * (item.cantidad || 1)
                  : (item.puntos_regalo || 0) * (item.cantidad || 1);

                return (
                  <div key={`${item.id}-${item.tipo}-${index}`} className="carrito-card-item">
                    {/* Item Thumbnail */}
                    <div className="carrito-card-thumb">
                      <img src={itemImg} alt={item.producto || item.nombre} />
                    </div>

                    {/* Item Info */}
                    <div className="carrito-card-details">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`carrito-tag ${isTicket ? 'tag-ticket' : 'tag-product'}`}>
                          {isTicket ? '🎟️ Ticket de Sorteo' : '📦 Producto Marketplace'}
                        </span>
                        {item.sku && (
                          <span className="text-xs text-gray-400 font-mono">SKU: {item.sku}</span>
                        )}
                      </div>

                      <h3 className="carrito-card-title">
                        {item.producto || item.nombre}
                      </h3>

                      <div className="flex items-baseline gap-2 mt-1">
                        <span className="text-sm text-gray-400">Precio unitario:</span>
                        <span className="text-sm font-semibold text-gray-200">
                          ${formatPrice(item.precio)}
                        </span>
                      </div>

                      {puntosItem > 0 && (
                        <div className="carrito-card-puntos-badge">
                          <span className="material-symbols-outlined text-xs">stars</span>
                          <span>Acumula +{puntosItem.toLocaleString()} pts</span>
                        </div>
                      )}
                    </div>

                    {/* Quantity Controls & Subtotal */}
                    <div className="carrito-card-actions">
                      <div className="carrito-quantity-control">
                        <button
                          type="button"
                          className="quantity-btn"
                          onClick={() => actualizarCantidad(item.id, item.tipo, item.cantidad - 1)}
                          disabled={item.cantidad <= 1}
                          aria-label="Disminuir cantidad"
                        >
                          <span className="material-symbols-outlined text-sm">remove</span>
                        </button>
                        <span className="quantity-value">{item.cantidad}</span>
                        <button
                          type="button"
                          className="quantity-btn"
                          onClick={() => actualizarCantidad(item.id, item.tipo, item.cantidad + 1)}
                          aria-label="Aumentar cantidad"
                        >
                          <span className="material-symbols-outlined text-sm">add</span>
                        </button>
                      </div>

                      <div className="carrito-card-subtotal">
                        <span className="subtotal-label">Subtotal</span>
                        <span className="subtotal-amount">${formatPrice(item.precio * item.cantidad)}</span>
                      </div>

                      <button
                        type="button"
                        className="btn-trash-item"
                        onClick={() => eliminarItem(item.id, item.tipo)}
                        title="Eliminar este artículo"
                      >
                        <span className="material-symbols-outlined">delete</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Bank Transfer Section (when requested) */}
            {mostrarDatosBancarios && cuentaActiva && (
              <div className="datos-transferencia-section" ref={datosBancariosRef}>
                <div className="datos-transferencia-card">
                  <div className="datos-card-top">
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-[#D4AF37] text-2xl">verified</span>
                      <div>
                        <h3 className="text-base font-bold text-white">Datos de Transferencia Bancaria</h3>
                        <p className="text-xs text-gray-400">Transfiere el monto exacto desde tu banco</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-copy-all"
                      onClick={copiarDatosBancarios}
                    >
                      <span className="material-symbols-outlined text-sm">
                        {copiado ? 'done_all' : 'content_copy'}
                      </span>
                      <span>{copiado ? '¡Datos Copiados!' : 'Copiar Todo'}</span>
                    </button>
                  </div>

                  <div className="datos-card-grid">
                    <div className="dato-cell">
                      <span className="dato-label">Banco</span>
                      <span className="dato-value font-bold text-[#b7f900]">{cuentaActiva.banco}</span>
                    </div>
                    <div className="dato-cell">
                      <span className="dato-label">Tipo de Cuenta</span>
                      <span className="dato-value">{cuentaActiva.tipo_cuenta}</span>
                    </div>
                    <div className="dato-cell col-span-2 sm:col-span-1">
                      <span className="dato-label">Número de Cuenta</span>
                      <div className="flex items-center justify-between">
                        <span className="dato-value font-mono font-bold text-white text-base">
                          {cuentaActiva.numero}
                        </span>
                        <button
                          type="button"
                          className="btn-copy-single"
                          onClick={copiarNumeroCuenta}
                          title="Copiar número de cuenta"
                        >
                          <span className="material-symbols-outlined text-xs">
                            {copiadoNumero ? 'check' : 'content_copy'}
                          </span>
                        </button>
                      </div>
                    </div>
                    <div className="dato-cell">
                      <span className="dato-label">Titular</span>
                      <span className="dato-value">{cuentaActiva.nombre}</span>
                    </div>
                    <div className="dato-cell">
                      <span className="dato-label">RUT</span>
                      <span className="dato-value font-mono">{cuentaActiva.rut}</span>
                    </div>
                    <div className="dato-cell col-span-2">
                      <span className="dato-label">Email de Confirmación</span>
                      <span className="dato-value font-mono text-xs sm:text-sm text-gray-300">{cuentaActiva.email}</span>
                    </div>
                  </div>

                  <div className="datos-instructions">
                    <div className="instruction-step">
                      <span className="step-circle">1</span>
                      <span>Abre tu app bancaria y transfiere <strong>${formatPrice(totalMonto)}</strong>.</span>
                    </div>
                    <div className="instruction-step">
                      <span className="step-circle">2</span>
                      <span>Pon en el asunto o mensaje tu nombre o correo.</span>
                    </div>
                    <div className="instruction-step">
                      <span className="step-circle">3</span>
                      <span>Presiona el botón verde <strong>"Transferencia Realizada"</strong> para validar.</span>
                    </div>
                  </div>

                  <div className="datos-card-footer">
                    <button type="button" className="btn btn-ghost text-gray-400 hover:text-white" onClick={cancelarPago}>
                      Modificar Carrito
                    </button>
                    <button type="button" className="btn btn-primary text-black font-bold px-6" onClick={handleAbrirModal}>
                      <span className="material-symbols-outlined text-lg mr-1">task_alt</span>
                      Transferencia Realizada
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column: Order Summary (Sticky) */}
          <div className="carrito-summary-column">
            <div className="carrito-summary-card">
              <h2 className="summary-title">Resumen de tu Compra</h2>

              <div className="summary-rows">
                <div className="summary-row">
                  <span className="text-gray-400">Subtotal de artículos</span>
                  <span className="text-white font-medium">${formatPrice(totalMonto)}</span>
                </div>

                <div className="summary-row">
                  <span className="text-gray-400">Envío / Entrega</span>
                  <span className="text-[#b7f900] font-semibold text-xs bg-[#b7f900]/10 px-2 py-0.5 rounded">
                    ¡GRATIS!
                  </span>
                </div>

                {totalPuntos > 0 && (
                  <div className="summary-row puntos-reward-row">
                    <div className="flex items-center gap-1.5 text-[#D4AF37]">
                      <span className="material-symbols-outlined text-sm">stars</span>
                      <span className="font-medium text-xs">Puntos de Recompensa</span>
                    </div>
                    <span className="font-bold text-[#D4AF37] text-sm">
                      +{totalPuntos.toLocaleString()} pts
                    </span>
                  </div>
                )}
              </div>

              <div className="summary-divider" />

              <div className="summary-total-row">
                <span className="text-base text-gray-200 font-semibold">Total a Pagar</span>
                <div className="text-right">
                  <span className="summary-total-amount">${formatPrice(totalMonto)}</span>
                  <span className="text-[11px] text-gray-400 block">Pesos Chilenos (CLP)</span>
                </div>
              </div>

              {/* Action Button */}
              {!mostrarDatosBancarios ? (
                <button
                  type="button"
                  className="btn-checkout-primary"
                  onClick={mostrarDatos}
                >
                  <span className="material-symbols-outlined">payments</span>
                  <span>Pagar con Transferencia</span>
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-checkout-primary pulse-green"
                  onClick={handleAbrirModal}
                >
                  <span className="material-symbols-outlined">task_alt</span>
                  <span>Confirmar Transferencia</span>
                </button>
              )}

              <p className="text-xs text-gray-400 text-center mt-3">
                Transferencia directa sin comisiones ocultas.
              </p>

              {/* Trust Badges */}
              <div className="summary-trust-badges">
                <div className="trust-item">
                  <span className="material-symbols-outlined">security</span>
                  <span>Compra 100% Protegida</span>
                </div>
                <div className="trust-item">
                  <span className="material-symbols-outlined">support_agent</span>
                  <span>Soporte WhatsApp 24/7</span>
                </div>
                <div className="trust-item">
                  <span className="material-symbols-outlined">award_star</span>
                  <span>Sorteos Oficiales Verificados</span>
                </div>
              </div>

              {/* Back to catalog link */}
              <div className="text-center mt-4">
                <Link to="/productos" className="text-xs text-[#b7f900] hover:underline inline-flex items-center gap-1">
                  <span className="material-symbols-outlined text-xs">arrow_back</span>
                  Seguir comprando más productos
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Carrito;
